import { Fragment, useMemo, useState } from 'react'
import type { JSX, ReactNode } from 'react'
import { api, call } from '@/api/client'
import type { Schema } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { useBrand } from '@/brand/context'
import { Button } from '@/ui/Button'
import { Field } from '@/ui/Field'
import { ErrorState, InlineError, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'
import { formOf, keyOf, settingsOf, specFor, targetsFor } from './spec'
import type { Form, FormValue, TargetField, TargetSpec } from './spec'

/**
 * Where a site's approved articles go — the setup card, the four targets as
 * data, and the summary/Edit states around them.
 *
 * Settings consolidation (2026-09-15): moved whole from `pages/seo/PublishTargets`,
 * which keeps the publishing half (documents, publications, rollback) and shows
 * the target read-only. This card already collapses to a summary on Save
 * (SEO-UI-119), which is the behaviour `SettingRow` gives the other settings,
 * so it is rendered directly by `/settings/publish-target` and by the wizard
 * step — `submitLabel` and `onSaved` are what those two callers add.
 */
type TargetOut = Schema<'PublishTargetOut'>

/**
 * One site's target, and the other three offered beside it.
 *
 * Its own component so switching site remounts it and a half-typed WordPress
 * password cannot survive into another site's form — the same reason
 * `RunTriggers` splits `TriggerCards` out.
 *
 * The read is re-issued whenever the tenant picks a different target, because
 * `?target=` is what returns *that* target's saved settings and *that* target's
 * `credential_set`. Without it the screen could list `configured_targets` and
 * say nothing about any of them, and switching back to a target would mean
 * retyping a configuration the row already holds.
 */
export function TargetSetup({
  siteId,
  domain,
  configurable,
  beside = null,
  submitLabel = 'Save',
  onSaved,
  onContinue,
}: {
  siteId: string
  domain: string
  configurable: boolean
  /**
   * Settings consolidation (2026-09-15). The wizard says *Continue* where the
   * panel says *Save*, and wants to move on once the row is written — the
   * response as stored, so a caller can read `credential_set` off it.
   */
  submitLabel?: string
  onSaved?: (saved: TargetOut) => void
  /**
   * The wizard's way past a target that is *already* connected (2026-09-15).
   *
   * A configured card collapses to its summary (SEO-UI-119), and the summary's
   * footer is *Edit*, *Send a test post* and *Disconnect* — every one of them
   * about changing the target, none about accepting it. A tenant who connected
   * a repository under Settings and then came back to the wizard's *Publishing*
   * step stood in front of a card that said *Connected* with nothing to press:
   * `onSaved` fires only on Save, and there was nothing to save. This renders
   * `submitLabel` as the primary in the collapsed footer, whenever the site
   * publishes somewhere, and calls it — the settings panel passes nothing and
   * keeps its footer as it was.
   */
  onContinue?: () => void
  /** SEO-UI-97. What sits under *Somewhere else* in the right-hand column —
   *  the failure alert, which is derived from a query this component does not
   *  own. Passed in rather than fetched here so the alert and the list at the
   *  bottom of the screen are one answer. */
  beside?: ReactNode
}): JSX.Element {
  const toast = useToast()
  const brand = useBrand()
  const targets = useMemo(() => targetsFor(brand.productName), [brand.productName])
  const [chosen, setChosen] = useState<string | null>(null)
  const [form, setForm] = useState<Form | null>(null)
  const [credential, setCredential] = useState('')
  const [seededFrom, setSeededFrom] = useState<TargetOut | null>(null)
  /** The sentence the last check came back with, or `null` if none has run for
   *  the target on screen. Held here rather than read off `check.state`, which
   *  carries a status and a failure and not the body. */
  const [checked, setChecked] = useState<string | null>(null)
  /**
   * Whether the form is open, or `null` to let the answer be derived.
   *
   * **SEO-UI-119.** The form used to be the only state this card had, so a
   * tenant who finished setting up a repository was left staring at the form
   * they had just filled in — with a *Save* button implying there was still
   * something to do, and a password box that reads as empty-and-therefore-
   * missing. A configured target now collapses to what it is set to, with
   * *Edit* to reopen.
   *
   * `null` rather than a boolean default because the honest answer depends on
   * the response: a target with nothing saved must open — there is nothing to
   * summarise and the tenant pressed *Set up* to fill it in — and one already
   * configured must not. It is reset to `null` on every response, which is what
   * makes the collapse after *Save* fall out rather than needing to be timed:
   * the refetch lands with `configured_targets` now naming this target, and the
   * derived answer flips on its own.
   */
  const [editing, setEditing] = useState<boolean | null>(null)

  const target = useQuery(
    (signal) =>
      call(() =>
        api.GET('/api/v1/seo/publish-targets/{site_id}', {
          params: {
            path: { site_id: siteId },
            ...(chosen === null ? {} : { query: { target: chosen } }),
          },
          signal,
        }),
      ),
    [siteId, chosen],
  )

  const save = useMutation((body: { target: string; settings: Record<string, unknown> }) =>
    call(() =>
      api.PUT('/api/v1/seo/publish-targets/{site_id}', {
        params: { path: { site_id: siteId } },
        body: credential.trim() === '' ? body : { ...body, credential: credential.trim() },
      }),
    ),
  )

  // SEO-UI-69, SEO-UI-101. *Send a test post*, which does not send a post — see
  // `Publisher.check`. `?target=` is passed explicitly because the tenant may be
  // looking at a target they have configured and not switched to, and checking
  // the one on screen is the only reading of the button that is not a trap.
  const check = useMutation((target: string) =>
    call(() =>
      api.POST('/api/v1/seo/publish-targets/{site_id}/check', {
        params: { path: { site_id: siteId }, query: { target } },
      }),
    ),
  )

  const disconnect = useMutation((target: string) =>
    call(() =>
      api.DELETE('/api/v1/seo/publish-targets/{site_id}', {
        params: { path: { site_id: siteId }, query: { target } },
      }),
    ),
  )

  const stored: TargetOut | null = target.state.data
  //: Which target the response in hand actually describes.
  const answered = stored?.viewing ?? stored?.target ?? null
  // **In flight until the answer is about the target on screen**, and this is
  // not the same question as `state.status === 'loading'`. Pressing *Switch
  // back* changes `chosen` at once, so there is a render where the new target's
  // labels are up and the query has not been re-issued yet — the old target's
  // response is still the data. Treating that render as ready puts a *Deploy
  // key* box under a *WordPress* heading for a frame, which is the one confusion
  // a credential field must never cause. Observed in a real browser against a
  // real API, not in the jsdom suite, where the stub resolves in a microtask.
  const pending = target.state.status === 'loading' || (chosen !== null && answered !== chosen)
  const spec = specFor(targets, pending ? chosen : answered)

  // Seeded during render rather than in an effect, on `BrandVoicePanel`'s
  // argument: an effect paints the defaults for one frame first, which shows a
  // repository address that is not the tenant's.
  //
  // **Keyed on the response object, not on the target's name**, and the
  // difference is a bug this shipped with once. Pressing *Switch back* changes
  // `chosen` immediately, so there is one render where the new target's fields
  // are on screen and the *old* target's response is still the query's data. A
  // key made of the chosen target and the request status matches in that render,
  // seeds the form from the wrong body, and then matches again when the real
  // answer lands — so the fields stay empty for ever. `useQuery` hands back a
  // new object per response, so identity seeds exactly once per answer, and the
  // spec is taken from the response's own `viewing` rather than from `chosen`
  // for the same reason: the form must describe the body it was filled from.
  if (stored !== null && stored !== seededFrom) {
    setSeededFrom(stored)
    setForm(formOf(specFor(targets, stored.viewing ?? stored.target), stored.settings ?? {}))
    setCredential('')
    // A check answers about one target. Leaving *"WordPress answered"* on
    // screen after *Switch back* has put Webflow's form up would be the same
    // confusion the `seededFrom` key exists to prevent, one card lower.
    setChecked(null)
    setEditing(null)
  }

  const values = form ?? formOf(spec, {})
  // Which field the credential box follows, resolved against the spec's own
  // fields rather than trusted (SEO-UI-100). `credentialAfter` naming a field
  // that does not exist would otherwise render the credential **nowhere at
  // all** — a target that silently cannot be given its secret, on a form that
  // still looks complete — and it is one rename of a field away. Falling back
  // to last is the behaviour every target without the key already has.
  const afterField =
    spec.fields.some((field) => field.name === spec.credentialAfter) &&
    spec.credentialAfter !== undefined
      ? spec.credentialAfter
      : null
  const isCurrent = stored?.target === spec.id
  const everConfigured = (stored?.configured_targets ?? []).includes(spec.id)
  /** Open while there is nothing saved to collapse to, and whenever *Edit* says so. */
  const open = editing ?? !everConfigured
  /** Whether this target has a secret at all — see `TargetSpec.credentialFree`. */
  const needsCredential = spec.credentialFree !== true

  /** The write-only credential box, built once and placed by `credentialAfter`
   *  (SEO-UI-100). A variable rather than a component so it keeps the closure
   *  over `credential` and `save` without a props list restating all of it. */
  const credentialField = !needsCredential ? null : (
    <Field
      label={spec.credentialLabel}
      help={`${spec.credentialHelp} Stored encrypted in Parameter Store, never in our database.`}
    >
      {(aria) => (
        <input
          className="ar-input ar-mono"
          type="password"
          autoComplete="new-password"
          {...aria}
          disabled={!configurable}
          value={credential}
          placeholder={
            stored?.credential_set === true ? 'Stored — leave blank to keep it' : 'Not stored yet'
          }
          onChange={(event) => {
            setCredential(event.currentTarget.value)
            save.reset()
          }}
        />
      )}
    </Field>
  )

  return (
    // SEO-UI-97. The design's two-column shape: the target being configured on
    // the left, and the stack of *"somewhere else"* plus the failure alert on
    // the right. This was one column of three stacked cards, which put the
    // other-targets list — a thing the tenant reads once, when switching —
    // between the form and the footer that saves it.
    <>
      <div className="ar-grid ar-grid--2">
        <section className="ar-card">
          <div className="ar-card__header">
            <h2>{spec.label}</h2>
            <span className="ar-card__spacer" />
            {/* SEO-UI-98, then parity P-TGT-03. Three states where the design
                draws one: `seo.html:1386` labels its single card *"✓ Connected"*
                because the prototype draws only the target in use, and that is
                the word the card in use carries. This screen can show any of the
                four — `?target=` is what makes *Switch back* possible — so the
                other two states keep their own words: *configured and dormant*
                is not *connected*. */}
            {isCurrent ? (
              <span className="ar-badge ar-badge--success">✓ Connected</span>
            ) : everConfigured ? (
              <span className="ar-badge ar-badge--neutral">Saved, not in use</span>
            ) : (
              <span className="ar-badge ar-badge--neutral">Not set up</span>
            )}
          </div>
          <div className="ar-card__body ar-stack">
            {pending ? (
              <Loading label="Loading this site’s publish target" />
            ) : target.state.status === 'error' ? (
              <ErrorState failure={target.state.failure} onRetry={target.refetch} />
            ) : (
              <>
                <InlineError failure={save.state.failure ?? disconnect.state.failure} />

                {/* SEO-UI-100. The credential sits where the spec says rather
                    than always last. `seo.html:1388-1395` puts WordPress's
                    application password third — Site URL, Username, password —
                    and *Publish as* after it, which groups the three fields
                    that answer *who is AugRank on your site* and leaves the one
                    that answers *what should it do there* on its own. Rendering
                    the credential after every setting split that group around
                    Categories and Author id. */}
                {!open ? (
                  <TargetSummary
                    spec={spec}
                    values={values}
                    credentialSet={stored?.credential_set === true}
                  />
                ) : (
                  <>
                    <SetupSteps spec={spec} />
                    {spec.fields.map((field) => (
                      <Fragment key={keyOf(field)}>
                        <SettingField
                          field={field}
                          value={values[keyOf(field)] ?? ''}
                          disabled={!configurable}
                          onChange={(next) => {
                            setForm({ ...values, [keyOf(field)]: next })
                            save.reset()
                          }}
                        />
                        {field.name === afterField ? credentialField : null}
                      </Fragment>
                    ))}
                    {afterField === null ? credentialField : null}

                    <p className="app-pub__note">
                      {!needsCredential
                        ? `${spec.label} authenticates to nothing, so there is no password, key or token to store — and nothing ${brand.productName} could lose.`
                        : stored?.credential_set === true
                          ? `A credential is stored for ${spec.label}. Leave the box empty to keep it, or type a new one to replace it.`
                          : `No credential is stored for ${spec.label} yet, so a publish to it would be refused until you add one.`}
                      {/* The address an operator needs during an incident — shown
                          only once something is at it. Under "No credential is
                          stored yet" the same sentence read as a place to go and
                          look for one. */}
                      {stored?.credential_set === true && stored.credential_path ? (
                        <>
                          {' '}
                          It lives at <code className="ar-mono">{stored.credential_path}</code> — an
                          address, not the secret.
                        </>
                      ) : null}
                    </p>
                  </>
                )}

                {/* SEO-UI-69. What the check reached, kept on screen rather
                    than shown as a toast: it names the site and the account,
                    which is the half a tenant has to read carefully — a
                    WordPress address typed one character wrong authenticates
                    perfectly against whatever is at it. */}
                {checked !== null ? (
                  <div className="ar-alert ar-alert--success" role="status">
                    <span className="ar-alert__icon" aria-hidden="true">
                      ✓
                    </span>
                    <div className="ar-alert__body">
                      <strong>{spec.label} answered</strong>
                      {checked}
                    </div>
                  </div>
                ) : null}
                <InlineError failure={check.state.failure} />
              </>
            )}
          </div>
          {configurable && !pending && target.state.status === 'ready' ? (
            // SEO-UI-101. The design's footer: a quiet test on the left, the
            // destructive one pushed to the right by a spacer so the two are
            // never adjacent. Save is the primary and keeps the left, because
            // unlike the prototype this card is also where a target is first
            // configured — there is nothing to test or disconnect until it is.
            <div className="ar-card__footer">
              {/* SEO-UI-119. *Save* while the form is open, *Edit* while it is
                  not. Two buttons in one slot rather than a form that is always
                  open, because the finished state of this card is a statement of
                  where the articles go — not a form still asking to be filled
                  in. */}
              {open ? (
                <>
                  <Button
                    tone="primary"
                    writes
                    busy={save.pending}
                    busyLabel="Saving"
                    onClick={() => {
                      void (async () => {
                        const result = await save.run({
                          target: spec.id,
                          settings: settingsOf(spec, values),
                        })
                        if (!result.ok) return
                        setCredential('')
                        // Collapsed here as well as by the refetch below, so the
                        // card closes under the finger that pressed Save rather
                        // than a round trip later. The response resets this to
                        // `null` when it lands and the derived answer agrees.
                        setEditing(false)
                        target.refetch()
                        // The toast tells the truth about the target as saved.
                        // A Git target saved without its deploy key is a
                        // target every publish will refuse, and the note on
                        // the form already says so — a green "will be pushed
                        // here from now on" over the top of it was the screen
                        // contradicting itself.
                        if (result.data.credential_required && !result.data.credential_set) {
                          toast.show({
                            kind: 'warning',
                            title: `${domain} is set to publish to ${spec.label}`,
                            body: 'No credential is stored yet, so nothing will be pushed until you add one and save again.',
                          })
                        } else {
                          toast.show({
                            kind: 'success',
                            title: `${domain} publishes to ${spec.label}`,
                            body: 'Approved articles will be pushed here from now on.',
                          })
                        }
                        onSaved?.(result.data)
                      })()
                    }}
                  >
                    {isCurrent ? submitLabel : `Publish to ${spec.label}`}
                  </Button>
                  {/* **The way back out of Edit, and it only exists where there is
                    something to go back to.** A first-time setup has no saved
                    state, so cancelling it would collapse the card to an empty
                    summary — on that path the way out is *Set up* on another
                    target, or leaving the screen. Discarding the typed values is
                    the whole job: re-seeding from `stored` is what makes an
                    abandoned edit leave nothing behind, including a half-typed
                    credential. */}
                  {everConfigured ? (
                    <Button
                      tone="ghost"
                      onClick={() => {
                        setForm(formOf(spec, stored?.settings ?? {}))
                        setCredential('')
                        setEditing(false)
                        save.reset()
                      }}
                    >
                      Cancel
                    </Button>
                  ) : null}
                </>
              ) : (
                <>
                  {/* `stored.target` rather than `isCurrent`: the site publishes
                      somewhere, which is what the wizard step asks, even when
                      the card on screen is a dormant target the tenant switched
                      away from. */}
                  {onContinue && stored?.target != null ? (
                    <Button tone="primary" onClick={onContinue}>
                      {submitLabel}
                    </Button>
                  ) : null}
                  <Button
                    tone="ghost"
                    onClick={() => {
                      setEditing(true)
                    }}
                  >
                    Edit
                  </Button>
                </>
              )}
              {/* Offered once there is something to test or remove. For the four
                  targets that authenticate that means a stored credential — a
                  check without one is refused by the publisher's own
                  constructor. For `download` there is no credential to wait for,
                  so being configured at all is the condition. */}
              {stored?.credential_set === true || (!needsCredential && everConfigured) ? (
                <>
                  <Button
                    tone="ghost"
                    size="sm"
                    busy={check.pending}
                    busyLabel="Checking"
                    onClick={() => {
                      void (async () => {
                        const result = await check.run(spec.id)
                        if (!result.ok) return
                        setChecked(result.data.detail)
                      })()
                    }}
                  >
                    {spec.checkLabel ?? 'Send a test post'}
                  </Button>
                  <span className="ar-card__spacer" />
                  <Button
                    tone="quiet"
                    size="sm"
                    writes
                    busy={disconnect.pending}
                    busyLabel="Disconnecting"
                    onClick={() => {
                      void (async () => {
                        const result = await disconnect.run(spec.id)
                        if (!result.ok) return
                        setCredential('')
                        setChecked(null)
                        check.reset()
                        target.refetch()
                        toast.show({
                          kind: 'success',
                          title: `${spec.label} disconnected`,
                          body: result.data.publishing
                            ? 'Its settings and its stored credential are gone. Nothing already published was touched.'
                            : `${domain} no longer publishes anywhere. Nothing already published was touched.`,
                        })
                      })()
                    }}
                  >
                    Disconnect
                  </Button>
                </>
              ) : null}
            </div>
          ) : null}
        </section>

        <div className="ar-stack">
          <section className="ar-card">
            <div className="ar-card__header">
              <h2>Somewhere else</h2>
            </div>
            <div className="ar-card__body ar-stack ar-stack--tight">
              {/* S-B38. Shown only while the site publishes nowhere, because
                  that is the one moment the choice is live — and the default
                  card on the left is the git one, which is the target a tenant
                  who cannot hand out access will bounce off. Saying it here is
                  cheaper than making them work out that the last row in this
                  list is the answer to the objection they already have. */}
              {(stored?.configured_targets ?? []).length === 0 ? (
                <p className="ar-text-body-sm ar-muted" style={{ margin: 0 }}>
                  Cannot give {brand.productName} access to your repository or CMS? Choose{' '}
                  <strong>Files to download</strong> — it needs no key, no token and no account, and
                  you place the article yourself.
                </p>
              ) : null}
              {targets
                .filter((other) => other.id !== spec.id)
                .map((other) => {
                  const saved = (stored?.configured_targets ?? []).includes(other.id)
                  return (
                    <div className="app-conn" key={other.id}>
                      <span className="app-conn__logo" aria-hidden="true">
                        {other.logo}
                      </span>
                      <span className="app-conn__body">
                        <strong>{other.label}</strong>
                        <span>
                          {saved
                            ? `Set up already — ${brand.productName} kept these settings when you switched away.`
                            : other.blurb}
                        </span>
                      </span>
                      {/* SEO-UI-99. `--sm` (2rem), as `seo.html:1409` draws it.
                        These are three rows of one repeated affordance inside a
                        secondary card; at the default 2.625rem each row's button
                        was the heaviest thing in the column. */}
                      <Button
                        tone="ghost"
                        size="sm"
                        onClick={() => {
                          setChosen(other.id)
                        }}
                      >
                        {saved ? 'Switch back' : 'Connect'}
                      </Button>
                    </div>
                  )
                })}
            </div>
          </section>
          {beside}
        </div>
      </div>
    </>
  )
}

/**
 * *How to set this up*, as numbered steps with the commands to run.
 *
 * **SEO-UI-118.** Open by default rather than behind a disclosure, and that is
 * the decision worth defending: a `<details>` is right for reference material a
 * reader returns to, and wrong for the thing they need *before* they can fill in
 * the form below it. Somebody who already knows scrolls past six lines; somebody
 * who does not would never have opened it. It disappears entirely once the
 * target is configured, because the card collapses to `TargetSummary`.
 *
 * The commands are selectable text rather than a copy button on purpose — a copy
 * button that silently fails (no clipboard permission, an insecure origin) is
 * worse than no button, and every one of these is short enough to select.
 */
function SetupSteps({ spec }: { spec: TargetSpec }): JSX.Element | null {
  if (spec.steps.length === 0) return null
  return (
    <section className="app-setup" aria-label={`How to set up ${spec.label}`}>
      <h2 className="app-setup__title">How to set this up</h2>
      <ol className="app-setup__steps">
        {spec.steps.map((step) => (
          <li key={step.text}>
            <span>{step.text}</span>
            {step.command === undefined ? null : (
              <code className="app-setup__cmd ar-mono">{step.command}</code>
            )}
            {step.fills === undefined ? null : (
              <span className="app-setup__fills">Goes in “{step.fills}” below</span>
            )}
          </li>
        ))}
      </ol>
    </section>
  )
}

/**
 * A configured target, collapsed to what it is set to.
 *
 * **SEO-UI-119**, and the reason it is a summary rather than a disabled form:
 * the two answer different questions. A disabled form still reads as *"fill this
 * in"* — every empty optional field looks unfinished and the credential box
 * looks missing — where what the tenant wants back from a card they have already
 * saved is one line per thing that is actually set. Unset optional fields are
 * therefore omitted rather than shown blank.
 *
 * **A credential is never shown, in any form.** Not masked, not a prefix, not a
 * length. What appears is that one is stored, which is all `credential_set` ever
 * says and the most a screen may know.
 */
function TargetSummary({
  spec,
  values,
  credentialSet,
}: {
  spec: TargetSpec
  values: Form
  credentialSet: boolean
}): JSX.Element {
  const set = spec.fields
    .map((field) => ({ field, value: values[keyOf(field)] }))
    .filter(({ field, value }) =>
      field.kind === 'switch' ? true : typeof value === 'string' && value.trim() !== '',
    )

  return (
    <dl className="app-summary">
      {set.map(({ field, value }) => (
        <Fragment key={keyOf(field)}>
          <dt>{field.label}</dt>
          <dd className={field.mono === true ? 'ar-mono' : undefined}>
            {field.kind === 'switch'
              ? value === true
                ? 'Yes'
                : 'No'
              : field.kind === 'select'
                ? // The option's own words, not the stored token: "publish" is
                  // the API's vocabulary and "Published — goes live immediately"
                  // is what the tenant chose. Read off `field` directly — going
                  // back through `spec.fields` by name would ignore `group` and
                  // could match a different field of the same name.
                  (field.options?.find((option) => option.value === value)?.label ?? String(value))
                : String(value)}
          </dd>
        </Fragment>
      ))}
      {spec.credentialFree === true ? null : (
        <>
          <dt>{spec.credentialLabel}</dt>
          <dd>
            {credentialSet ? (
              <span className="ar-badge ar-badge--success">Stored</span>
            ) : (
              <span className="ar-badge ar-badge--danger">Not stored — a publish would fail</span>
            )}
          </dd>
        </>
      )}
    </dl>
  )
}

/** One setting, in whichever control its kind calls for. */
function SettingField({
  field,
  value,
  disabled,
  onChange,
}: {
  field: TargetField
  value: FormValue
  disabled: boolean
  onChange: (next: FormValue) => void
}): JSX.Element {
  if (field.kind === 'switch') {
    return (
      <label className="app-pub__switch">
        <input
          type="checkbox"
          checked={value === true}
          disabled={disabled}
          onChange={(event) => {
            onChange(event.currentTarget.checked)
          }}
        />
        <span>
          <strong>{field.label}</strong>
          {field.help === undefined ? null : <span className="ar-subtle">{field.help}</span>}
        </span>
      </label>
    )
  }
  return (
    <Field label={field.label} help={field.help} required={field.required}>
      {(aria) =>
        field.kind === 'select' ? (
          <select
            className="ar-select"
            {...aria}
            disabled={disabled}
            value={typeof value === 'string' ? value : ''}
            onChange={(event) => {
              onChange(event.currentTarget.value)
            }}
          >
            {(field.options ?? []).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        ) : (
          <input
            className={field.mono === true ? 'ar-input ar-mono' : 'ar-input'}
            {...aria}
            disabled={disabled}
            inputMode={field.kind === 'number' ? 'numeric' : undefined}
            placeholder={field.placeholder}
            value={typeof value === 'string' ? value : ''}
            onChange={(event) => {
              onChange(event.currentTarget.value)
            }}
          />
        )
      }
    </Field>
  )
}

/* --- the failure the design puts beside the form -------------------------------- */
